import axios from 'axios';
import { readDate, writeDate } from '../../utils/appDate';
import { constants } from '../../Helpers/constantsFile';

/**
 * Every call the customers book makes, in one place.
 *
 * All of them are restricted server-side to manager, cashier, accountant and
 * superadmin — a waiter cannot reach any of it, which is why the tab is not in
 * their sidebar either.
 */

const auth = (token) => ({ headers: { authorization: token } });

export function fetchCustomers({ businessId, token }) {
  return axios
    .get(`${constants.baseUrl}/customers/get-business-customers/${businessId}`, auth(token))
    .then((res) => res.data?.data?.customers || []);
}

export function createCustomer({ payload, token }) {
  return axios
    .post(`${constants.baseUrl}/customers`, payload, auth(token))
    .then((res) => res.data?.data?.customer);
}

export function updateCustomer({ id, payload, token }) {
  return axios
    .patch(`${constants.baseUrl}/customers/${id}`, payload, auth(token))
    .then((res) => res.data?.data?.customer);
}

export function fetchTransactions({ customerId, token }) {
  return axios
    .get(
      `${constants.baseUrl}/transactions/get-customer-transactions/${customerId}`,
      auth(token)
    )
    .then((res) => res.data?.data?.transactions || []);
}

/**
 * One entry in a customer's book.
 *
 * The sign is the whole thing:
 *   `debit`  — Deen Cusub, a charge. The server moves the balance UP.
 *   `credit` — Bixin, a payment. The balance goes DOWN.
 *
 * `business` is deliberately not sent: createTransactionFn fills it from the
 * customer, and one source is better than two that can disagree.
 *
 * `transactionType: 'custom'` is what separates a hand-entered line from the
 * ones an order or an expense writes by itself.
 */
export function createTransaction({ customerId, userId, kind, amount, description, date, token }) {
  const value = Number(amount);

  const payload = {
    customer: customerId,
    user: userId,
    transactionType: 'custom',
    description,
    date: writeDate(date),
    debit: kind === 'charge' ? value : 0,
    credit: kind === 'payment' ? value : 0,
  };

  return axios
    .post(`${constants.baseUrl}/transactions`, payload, auth(token))
    .then((res) => res.data?.data?.transaction);
}

/**
 * Change a line that is already in the book.
 *
 * Only the three things a person can see and get wrong: what it was for, how
 * much, and when. The side it sits on is not editable — flipping a charge into
 * a payment is a different entry, not a correction, and doing it by accident
 * moves the balance by twice the amount.
 *
 * The server reads the before and after figures and applies the difference, so
 * a charge corrected from $50 to $40 moves the balance by $10, not $40.
 */
export function updateTransaction({ id, amount, description, date, kind, token }) {
  const value = Number(amount);

  const payload = {
    description,
    date: writeDate(date),
    debit: kind === 'charge' ? value : 0,
    credit: kind === 'payment' ? value : 0,
  };

  return axios
    .patch(`${constants.baseUrl}/transactions/${id}`, payload, auth(token))
    .then((res) => res.data?.data?.transaction);
}

/**
 * Remove a line.
 *
 * A soft delete on the server: the row is kept but marked `deleted` and cut
 * loose from the customer, and the balance it moved is put back. It stops
 * appearing because the read filters `status` out — so the book still knows
 * this happened even though it no longer shows.
 */
export function deleteTransaction({ id, token }) {
  return axios
    .delete(`${constants.baseUrl}/transactions/${id}`, auth(token))
    .then((res) => res.data?.data?.transaction);
}

/**
 * The running balance, oldest first.
 *
 * The server stores each line but not the balance after it, so the column is
 * derived here — the same thing the mobile app does in
 * `Transaction.transactionsWithBalance`.
 *
 * Dates arrive in more than one shape — an order writes a wall-clock string,
 * this screen writes the same shape through `writeDate` — but they are all the
 * same fiction, so `readDate` puts every line on one clock before they are
 * compared. Sorting on raw values would interleave the two shapes wrongly.
 */
export function withRunningBalance(transactions) {
  const sorted = (transactions || [])
    .slice()
    .sort((a, b) => readDate(a.date).valueOf() - readDate(b.date).valueOf());

  let running = 0;
  return sorted.map((transaction) => {
    running += Number(transaction.debit || 0) - Number(transaction.credit || 0);
    return {
      ...transaction,
      // Rounded at each step, not at the end: a book that shows 46.00 and
      // 71.50 must not total 117.49999999999999.
      balance: Math.round(running * 100) / 100,
    };
  });
}
